import { Box, Flex, Button, IconButton, Portal, Heading } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { JGoogleIcon } from '../../components'
import betaNotifStore from '../../stores/beta-notif.store'

const STORAGE_KEY_DAILY = 'beta_banner_last_closed'
const STORAGE_KEY_PERMANENT = 'beta_banner_permanently_dismissed'

const BetaNotif = () => {
    const { t } = useTranslation()
    const { setHeight } = betaNotifStore()
    const [isVisible, setIsVisible] = useState(false)
    const bannerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const checkVisibility = () => {
            const todayDate = new Date().toLocaleDateString()
            const isPermanentlyDismissed = localStorage.getItem(STORAGE_KEY_PERMANENT)
            const lastClosedDate = localStorage.getItem(STORAGE_KEY_DAILY)

            if (isPermanentlyDismissed) {
                return
            }

            if (lastClosedDate === todayDate) {
                return
            }

            setIsVisible(true)
        }

        checkVisibility()
    }, [])

    /**
     * Laporkan tinggi banner ke store supaya layout lain (sidebar)
     * bisa menyisakan ruang, tingginya ikut berubah saat teks wrap
     */
    useEffect(() => {
        const banner = bannerRef.current

        if (!isVisible || !banner) {
            setHeight(0)
            return
        }

        const observer = new ResizeObserver(() => setHeight(banner.offsetHeight))
        observer.observe(banner)
        setHeight(banner.offsetHeight)

        return () => {
            observer.disconnect()
            setHeight(0)
        }
    }, [isVisible, setHeight])

    const handleCloseToday = () => {
        const todayDate = new Date().toLocaleDateString()
        localStorage.setItem(STORAGE_KEY_DAILY, todayDate)
        setIsVisible(false)
    }

    const handleDontShowAgain = () => {
        localStorage.setItem(STORAGE_KEY_PERMANENT, 'true')
        setIsVisible(false)
    }

    if (!isVisible) return null

    return (
        <Portal>
            <AnimatePresence>
                <Box
                    position="fixed"
                    bottom="0"
                    zIndex="toast"
                    w='100%'
                    as={motion.div}
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                >
                    <Flex
                        ref={bannerRef}
                        bg="primary.90"
                        color="white"
                        py="8px"
                        px="12px"
                        borderRadius="10px 10px 0 0"
                        boxShadow="2xl"
                        align="center"
                        justify="space-between"
                        gap="4"
                        flexWrap={{ base: 'wrap', md: 'nowrap' }}
                    >
                        <Flex align="center" gap="3" flex="1">
                            <Box>
                                <Flex align="center" gap={2} wrap="wrap">
                                    <Heading size="chakra_label_medium" color="primary.20">
                                        {t('BetaNotif.Title')}
                                    </Heading>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        border='1px'
                                        borderColor="primary.20"
                                        rounded='full'
                                        height="20px"
                                        _hover={{ bg: 'whiteAlpha.200' }}
                                        onClick={() => window.open('https://beta.jarivis.com', '_blank', 'noopener,noreferrer')}
                                        rightIcon={<JGoogleIcon name='open_in_new' fontSize="16px" />}
                                    >
                                        <Heading size="chakra_label_small" color="primary.20">
                                            {t('BetaNotif.ExploreBeta')}
                                        </Heading>
                                    </Button>
                                </Flex>
                                <Heading size="chakra_body_small" color="primary.20" mt="2px">
                                    {t('BetaNotif.Description')}
                                </Heading>
                            </Box>
                        </Flex>

                        <Flex align="center" gap="4">
                            <Button
                                variant="link"
                                size="xs"
                                color="primary.20"
                                fontWeight="normal"
                                textDecoration="underline"
                                opacity={0.7}
                                _hover={{ opacity: 1 }}
                                onClick={handleDontShowAgain}
                            >
                                <Heading size="chakra_body_small" color="primary.20">
                                    {t('BetaNotif.DontShowAgain')}
                                </Heading>
                            </Button>

                            <IconButton
                                aria-label={t('BetaNotif.Close')}
                                icon={<JGoogleIcon name="close" fontSize="18px" color="primary.20" />}
                                size="xs"
                                variant="ghost"
                                _hover={{ bg: 'whiteAlpha.200' }}
                                onClick={handleCloseToday}
                            />
                        </Flex>
                    </Flex>
                </Box>
            </AnimatePresence>
        </Portal>
    )
}

export default BetaNotif
